// Mock data for local development without backend
import type { AppStatus, DashboardResponse, DeploymentsResponse, AppCostSummary, TenantCostSummary, MonthlyCost } from './api'

export const MOCK_MODE = import.meta.env.VITE_MOCK_MODE === 'true'

export const mockApps: AppStatus[] = [
  {
    id: 'manager',
    tenant_id: 'fact',
    display_name: 'Manager',
    environments: {
      dev: { status: 'healthy', version: 'v1.2.3', deployed_at: new Date().toISOString() }
    },
    services: ['cloud_run', 'neon', 'cloudflare'],
    current_month_cents: 1420,
    currency: 'EUR'
  },
  {
    id: 'star-go',
    tenant_id: 'fact',
    display_name: 'Star Go',
    environments: {
      dev: { status: 'healthy', version: 'v2.0.1', deployed_at: new Date().toISOString() },
      stg: { status: 'healthy', version: 'v2.0.0', deployed_at: new Date(Date.now() - 3600000).toISOString() },
      prod: { status: 'healthy', version: 'v1.9.0', deployed_at: new Date(Date.now() - 86400000).toISOString() }
    },
    services: ['cloud_run', 'neon', 'cloudflare', 'mail', 'gcs'],
    current_month_cents: 3850,
    currency: 'EUR'
  },
  {
    id: 'demo-app',
    tenant_id: 'demo',
    display_name: 'Demo App',
    environments: {
      dev: { status: 'healthy', version: 'v0.1.0', deployed_at: new Date().toISOString() }
    },
    services: ['cloud_run', 'neon'],
    current_month_cents: 450,
    currency: 'EUR'
  }
]

export const mockDashboard: DashboardResponse = {
  apps: mockApps,
  updated_at: new Date().toISOString()
}

export const mockDeployments: DeploymentsResponse = {
  deployments: [
    {
      id: '1',
      app_id: 'star-go',
      environment: 'dev',
      sha: 'abc123',
      status: 'success',
      started_at: new Date().toISOString(),
      duration_ms: 120000,
      actor: 'github-actions',
      run_url: 'https://github.com/bigap-it/fact-star-go/actions/runs/123'
    }
  ],
  total_count: 1,
  page: 1,
  page_size: 10
}

export const mockUser = {
  uid: 'mock-user',
  email: 'dev@localhost',
  displayName: 'Dev User',
  photoURL: null,
  getIdToken: async () => 'mock-token'
}

// Generate last 12 months for cost history
function getLast12Months(): string[] {
  const months: string[] = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return months
}

function generateMockMonthlyCosts(baseCosts: Record<string, number>): MonthlyCost[] {
  const months = getLast12Months()
  return months.map(month => {
    const byService: Record<string, number> = {}
    let total = 0
    for (const [service, base] of Object.entries(baseCosts)) {
      const variance = 0.8 + Math.random() * 0.4
      const cost = Math.round(base * variance)
      byService[service] = cost
      total += cost
    }
    return {
      year_month: month,
      total_cents: total,
      by_service: byService,
      currency: 'EUR'
    }
  })
}

export function mockAppCosts(appId: string): AppCostSummary {
  const baseCosts: Record<string, Record<string, number>> = {
    'manager': { cloud_run: 800, neon: 500, gcs: 100 },
    'star-go': { cloud_run: 1500, neon: 2000, gcs: 300, mail: 50 },
    'demo-app': { cloud_run: 300, neon: 150 }
  }

  const costs = baseCosts[appId] || { cloud_run: 500, neon: 300 }
  const history = generateMockMonthlyCosts(costs)

  return {
    app_id: appId,
    current_month_cents: history[0]?.total_cents || 0,
    currency: 'EUR',
    monthly_history: history
  }
}

export function mockTenantCosts(): TenantCostSummary {
  const appCosts = mockApps
    .filter(app => app.tenant_id === 'fact')
    .map(app => mockAppCosts(app.id))

  const months = getLast12Months()
  const monthlyHistory: MonthlyCost[] = months.map(month => {
    const byService: Record<string, number> = {}
    let total = 0

    for (const app of appCosts) {
      const monthData = app.monthly_history.find(m => m.year_month === month)
      if (monthData) {
        for (const [service, cost] of Object.entries(monthData.by_service)) {
          byService[service] = (byService[service] || 0) + cost
          total += cost
        }
      }
    }

    return {
      year_month: month,
      total_cents: total,
      by_service: byService,
      currency: 'EUR'
    }
  })

  return {
    tenant_id: 'fact',
    current_month_cents: monthlyHistory[0]?.total_cents || 0,
    currency: 'EUR',
    monthly_history: monthlyHistory,
    by_app: appCosts
  }
}
